/**
 * @file useCatalogLocations.js
 * @brief Manages paginated location CRUD state for the catalog admin interface.
 */
import { useCallback, useEffect, useState } from "react";
import { FIELD_LIMITS } from "../constants/fieldLimits";

import { fetchPaginated } from "../components/CatalogAdmin/utils";


/**
 * Maintain location administration state, pagination, and CRUD handlers.
 * @param {Object} params Hook parameters.
 * @param {Object} params.api API client used for location requests.
 * @param {Function} params.t Translation helper.
 * @returns {Object} Location list state and admin action handlers.
 */
export default function useCatalogLocations({ api, t }) {
    const [locations, setLocations] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [next, setNext] = useState(null);
    const [previous, setPrevious] = useState(null);
    const [search, setSearch] = useState("");
    const [name, setName] = useState("");
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const loadLocations = useCallback(async (targetPage = 1, targetSearch = "") => {
        setLoading(true);
        setError(null);
        try {
            const payload = await fetchPaginated(api, "locations/", {
                page: targetPage,
                search: targetSearch || undefined,
            });
            setLocations(payload.items);
            setCount(payload.count);
            setNext(payload.next);
            setPrevious(payload.previous);
            setPage(targetPage);
        } catch (requestError) {
            console.error(requestError);
            setError(t("catalogAdmin.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [api, t]);

    useEffect(() => {
        loadLocations(1, "");
    }, [loadLocations]);

    const handleAdd = useCallback(async (event) => {
        event.preventDefault();
        if (!name.trim()) {
            return;
        }
        if (name.trim().length > FIELD_LIMITS.locationName) {
            setError(t("catalogAdmin.locationNameTooLong", { max: FIELD_LIMITS.locationName }));
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await api.post("locations/", { name: name.trim() });
            setName("");
            await loadLocations(1, search);
        } catch (requestError) {
            setError(requestError.response?.data?.name?.[0] ?? t("catalogAdmin.addLocationFailed"));
        } finally {
            setSaving(false);
        }
    }, [api, loadLocations, name, search, t]);

    const handleSave = useCallback(async (locationId) => {
        if (!editName.trim()) {
            return;
        }
        if (editName.trim().length > FIELD_LIMITS.locationName) {
            setError(t("catalogAdmin.locationNameTooLong", { max: FIELD_LIMITS.locationName }));
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await api.patch(`locations/${locationId}/`, { name: editName.trim() });
            setEditId(null);
            setEditName("");
            await loadLocations(page, search);
        } catch (requestError) {
            setError(requestError.response?.data?.name?.[0] ?? t("catalogAdmin.updateFailed"));
        } finally {
            setSaving(false);
        }
    }, [api, editName, loadLocations, page, search, t]);

    const handleDelete = useCallback(async (locationId) => {
        if (!window.confirm(t("catalogAdmin.deleteLocationConfirm"))) {
            return;
        }

        setError(null);
        try {
            await api.delete(`locations/${locationId}/`);
            await loadLocations(page, search);
        } catch {
            setError(t("catalogAdmin.deleteFailed"));
        }
    }, [api, loadLocations, page, search, t]);

    return {
        locations,
        count,
        page,
        next,
        previous,
        search,
        setSearch,
        name,
        setName,
        editId,
        setEditId,
        editName,
        setEditName,
        loading,
        saving,
        error,
        loadLocations,
        handleAdd,
        handleSave,
        handleDelete,
    };
}
